import type { Viewer } from '../../../viewer/Viewer';
import * as THREE from 'three/webgpu';
import { AssemblyComponentFixationService } from '../../component-fixation';
import { recomputeAssemblyDatumReferences } from '../../../reference/shared/recomputeAssemblyDatumReferences';
import {
  applyComponentInstanceSnapshotToObject,
  createAssemblyTransformHistoryRecord,
  createComponentInstanceSnapshotFromObject,
  type AssemblyTransformHistoryRecord,
  type ComponentInstanceSnapshot,
} from '../../component-instance';
import { MateSolveStatus } from '../../definition';
import { resolveMoveComponentTransformTarget } from '../MoveComponentTransformTargetResolver';
import { AssemblyTransformToolMode } from '../MoveComponentTypes';
import { createAssemblyComponentDrivenConstraintsForToolMode } from '../AssemblyComponentTransformIntent';

/**
 * 目标对象预览快照。
 */
interface MovePreviewSnapshot {
  /** 当前预览绑定对象。 */
  target: THREE.Object3D;
  /** 初始世界位置（在对象本地 position 语义下记录）。 */
  initialPosition: THREE.Vector3;
  /** CAD 历史使用的完整组件实例 before 快照。 */
  initialComponentSnapshot: ComponentInstanceSnapshot;
}

/**
 * 移动服务。
 *
 * 设计说明：
 * 1. 该服务聚焦移动求解职责，不承载 UI 状态；
 * 2. 第一阶段先打通控制链路，几何求解在后续迭代补全；
 * 3. 对外接口保持稳定，避免上层控制器后续频繁改动。
 */
export class MoveService {
  private readonly viewer: Viewer;
  private previewSnapshot: MovePreviewSnapshot | null = null;
  /** Mate 实时求解涉及组件的拖拽初始快照。 */
  private readonly mateSolveInitialSnapshots = new Map<string, ComponentInstanceSnapshot>();
  /** Mate 实时求解涉及组件的最新快照。 */
  private readonly mateSolveLatestSnapshots = new Map<string, ComponentInstanceSnapshot>();
  /** 装配体坐标转换临时四元数。 */
  private readonly assemblyWorldQuaternion = new THREE.Quaternion();
  /** 父节点世界四元数（逆）临时变量。 */
  private readonly parentWorldQuaternionInverse = new THREE.Quaternion();
  /** 装配体本地坐标增量临时向量。 */
  private readonly assemblyLocalDelta = new THREE.Vector3();
  /** 世界坐标增量临时向量。 */
  private readonly worldDelta = new THREE.Vector3();
  /** 目标父节点本地坐标增量临时向量。 */
  private readonly parentLocalDelta = new THREE.Vector3();
  /** 目标世界坐标临时向量。 */
  private readonly targetWorldPosition = new THREE.Vector3();
  /** 目标父节点局部坐标临时向量。 */
  private readonly targetParentLocalPosition = new THREE.Vector3();
  /** 当前世界坐标读取临时向量。 */
  private readonly currentWorldPosition = new THREE.Vector3();

  /**
   * 创建移动服务。
   *
   * @param viewer 场景视图实例。
   */
  public constructor(viewer: Viewer) {
    this.viewer = viewer;
  }

  /**
   * 预览 Delta XYZ 移动。
   *
   * @param delta 三轴增量。
   */
  public previewMoveByDelta(delta: readonly [number, number, number]): void {
    const snapshot = this.ensurePreviewSnapshot();
    if (!snapshot) {
      return;
    }

    snapshot.target.position.set(
      snapshot.initialPosition.x + delta[0],
      snapshot.initialPosition.y + delta[1],
      snapshot.initialPosition.z + delta[2],
    );
    snapshot.target.updateMatrixWorld(true);
    this.solveCommittedMatesForPreview(snapshot);
    this.recomputeDependentAssemblyDatumReferences();
    this.requestRenderAfterTransform();
  }

  /**
   * 预览沿装配体本地坐标（X/Y/Z）移动。
   *
   * 设计说明：
   * 1. 输入 delta 定义在“装配体本地坐标系”；
   * 2. 若目标对象存在父节点，需要先将装配体本地增量转换到父节点本地；
   * 3. 位姿更新始终基于快照初始位姿，避免累积漂移。
   *
   * @param delta 装配体本地三轴增量。
   */
  public previewMoveByAssemblyLocalDelta(delta: readonly [number, number, number]): void {
    const snapshot = this.ensurePreviewSnapshot();
    if (!snapshot) {
      return;
    }

    //------ 沿装配体坐标移动收口 开始------
    // delta 在装配体本地坐标下，先转世界坐标。
    this.assemblyLocalDelta.set(delta[0], delta[1], delta[2]);
    this.viewer.scene.getWorldQuaternion(this.assemblyWorldQuaternion);
    this.worldDelta.copy(this.assemblyLocalDelta).applyQuaternion(this.assemblyWorldQuaternion);

    // 再从世界坐标转到目标父节点本地坐标。
    if (snapshot.target.parent) {
      snapshot.target.parent.getWorldQuaternion(this.parentWorldQuaternionInverse);
      this.parentWorldQuaternionInverse.invert();
      this.parentLocalDelta.copy(this.worldDelta).applyQuaternion(this.parentWorldQuaternionInverse);

      snapshot.target.position.set(
        snapshot.initialPosition.x + this.parentLocalDelta.x,
        snapshot.initialPosition.y + this.parentLocalDelta.y,
        snapshot.initialPosition.z + this.parentLocalDelta.z,
      );
    } else {
      // 无父节点时 position 即世界空间位置，直接叠加世界增量。
      snapshot.target.position.set(
        snapshot.initialPosition.x + this.worldDelta.x,
        snapshot.initialPosition.y + this.worldDelta.y,
        snapshot.initialPosition.z + this.worldDelta.z,
      );
    }
    //------ 沿装配体坐标移动收口 结束------

    snapshot.target.updateMatrixWorld(true);
    this.solveCommittedMatesForPreview(snapshot);
    this.recomputeDependentAssemblyDatumReferences();
    this.requestRenderAfterTransform();
  }

  /**
   * 预览移动到指定世界坐标。
   *
   * @param position 目标坐标。
   */
  public previewMoveToPosition(position: readonly [number, number, number]): void {
    const snapshot = this.ensurePreviewSnapshot();
    if (!snapshot) {
      return;
    }

    //------ 装配体绝对坐标移动收口 开始------
    // 到 XYZ 位置输入的是装配体世界坐标。
    // 当目标存在父节点时，必须先转成父节点局部坐标再写入 position。
    this.targetWorldPosition.set(position[0], position[1], position[2]);
    if (snapshot.target.parent) {
      this.targetParentLocalPosition.copy(this.targetWorldPosition);
      snapshot.target.parent.worldToLocal(this.targetParentLocalPosition);
      snapshot.target.position.copy(this.targetParentLocalPosition);
    } else {
      snapshot.target.position.copy(this.targetWorldPosition);
    }
    //------ 装配体绝对坐标移动收口 结束------

    snapshot.target.updateMatrixWorld(true);
    this.solveCommittedMatesForPreview(snapshot);
    this.recomputeDependentAssemblyDatumReferences();
    this.requestRenderAfterTransform();
  }

  /**
   * 读取当前选中目标的装配体世界坐标。
   *
   * @returns 当前选中目标的世界坐标；无目标时返回原点。
   */
  public readSelectedTargetWorldPosition(): readonly [number, number, number] {
    const selectedTarget = resolveMoveComponentTransformTarget(
      this.viewer.getSelectedObjects()[0] ?? null,
      this.viewer,
    );
    if (!selectedTarget) {
      return [0, 0, 0] as const;
    }

    selectedTarget.getWorldPosition(this.currentWorldPosition);
    return [
      this.currentWorldPosition.x,
      this.currentWorldPosition.y,
      this.currentWorldPosition.z,
    ] as const;
  }

  /**
   * 提交移动预览。
   */
  public commitPreview(): AssemblyTransformHistoryRecord | null {
    // 提交时保留当前位姿，仅清空会话快照。
    if (!this.previewSnapshot) {
      return null;
    }

    if (!this.solveCommittedMatesForPreview(this.previewSnapshot, { updateDocumentStatus: true })) {
      return null;
    }

    const afterSnapshot = createComponentInstanceSnapshotFromObject({
      object: this.previewSnapshot.target,
      assemblyId: this.previewSnapshot.initialComponentSnapshot.assemblyId,
      componentInstanceId: this.previewSnapshot.initialComponentSnapshot.componentInstanceId,
    });
    const record = createAssemblyTransformHistoryRecord(
      'move',
      this.previewSnapshot.initialComponentSnapshot,
      afterSnapshot,
    );
    const relatedRecords = this.createMateSolveRelatedRecords('move', afterSnapshot.componentInstanceId);

    this.previewSnapshot = null;
    this.clearMateSolveSnapshots();

    if (!record) {
      return relatedRecords[0] && relatedRecords.length > 1
        ? {
            ...relatedRecords[0],
            relatedRecords: relatedRecords.slice(1),
          }
        : relatedRecords[0] ?? null;
    }

    return relatedRecords.length > 0
      ? {
          ...record,
          relatedRecords,
        }
      : record;
  }

  /**
   * 回滚移动预览。
   */
  public cancelPreview(): void {
    if (!this.previewSnapshot) {
      return;
    }
    applyComponentInstanceSnapshotToObject(
      this.previewSnapshot.target,
      this.previewSnapshot.initialComponentSnapshot,
    );
    this.rollbackMateSolvedPreviewObjects();
    this.previewSnapshot.target.updateMatrixWorld(true);
    this.recomputeDependentAssemblyDatumReferences();
    this.updateChangedInstanceMatrices();
    this.previewSnapshot = null;
    this.clearMateSolveSnapshots();
    this.viewer.requestRender();
  }

  /**
   * 清理移动服务资源。
   */
  public dispose(): void {
    this.cancelPreview();
  }

  /**
   * 确保当前存在有效预览快照。
   *
   * @returns 可用快照；无目标对象时返回 null。
   */
  private ensurePreviewSnapshot(): MovePreviewSnapshot | null {
    const selectedTarget = resolveMoveComponentTransformTarget(
      this.viewer.getSelectedObjects()[0] ?? null,
      this.viewer,
    );
    if (!selectedTarget) {
      return null;
    }
    if (!AssemblyComponentFixationService.canTransformSceneObject(selectedTarget)) {
      // SolidWorks 中只有明确浮动的零部件才能被“移动零部件”命令改变位置；这里在会话入口收口。
      if (this.previewSnapshot?.target === selectedTarget) {
        this.previewSnapshot = null;
      }
      return null;
    }

    if (this.previewSnapshot?.target === selectedTarget) {
      return this.previewSnapshot;
    }

    this.previewSnapshot = {
      target: selectedTarget,
      initialPosition: selectedTarget.position.clone(),
      initialComponentSnapshot: createComponentInstanceSnapshotFromObject({
        object: selectedTarget,
      }),
    };
    this.clearMateSolveSnapshots();
    return this.previewSnapshot;
  }

  /**
   * 针对当前移动预览重求解已提交配合。
   *
   * @param snapshot 当前移动预览快照。
   */
  private solveCommittedMatesForPreview(
    snapshot: MovePreviewSnapshot,
    options: { readonly updateDocumentStatus?: boolean } = {},
  ): boolean {
    const solveResult = this.viewer.assemblyMateController?.solveCommittedMatesAfterComponentTransform({
      anchorComponentInstanceId: snapshot.initialComponentSnapshot.componentInstanceId,
      drivenComponentConstraints: createAssemblyComponentDrivenConstraintsForToolMode(
        snapshot.initialComponentSnapshot.componentInstanceId,
        AssemblyTransformToolMode.Move,
      ),
      /**
       * 预览阶段只需要把约束解算结果应用到场景对象；配合文档状态应留给最终提交或显式诊断刷新写入。
       * 该参数由提交入口显式打开，避免 pointermove 高频路径触发持久化保存。
       */
      updateDocumentStatus: options.updateDocumentStatus ?? false,
    }) ?? null;
    if (!solveResult) {
      return true;
    }
    if (solveResult.status !== MateSolveStatus.Solved) {
      applyComponentInstanceSnapshotToObject(snapshot.target, snapshot.initialComponentSnapshot);
      this.rollbackMateSolvedPreviewObjects();
      this.mateSolveLatestSnapshots.clear();
      return false;
    }

    solveResult.beforeSnapshots.forEach((beforeSnapshot) => {
      if (beforeSnapshot.componentInstanceId === snapshot.initialComponentSnapshot.componentInstanceId) {
        return;
      }
      if (!this.mateSolveInitialSnapshots.has(beforeSnapshot.componentInstanceId)) {
        this.mateSolveInitialSnapshots.set(beforeSnapshot.componentInstanceId, beforeSnapshot);
      }
    });
    solveResult.afterSnapshots.forEach((afterSnapshot) => {
      if (afterSnapshot.componentInstanceId === snapshot.initialComponentSnapshot.componentInstanceId) {
        return;
      }
      this.mateSolveLatestSnapshots.set(afterSnapshot.componentInstanceId, afterSnapshot);
    });
    return true;
  }

  /**
   * 创建 Mate 实时求解产生的联动历史记录。
   *
   * @param operation 操作类型。
   * @param primaryComponentInstanceId 主操作组件 ID。
   * @returns 联动历史记录。
   */
  private createMateSolveRelatedRecords(
    operation: 'move',
    primaryComponentInstanceId: string,
  ): AssemblyTransformHistoryRecord[] {
    return [...this.mateSolveInitialSnapshots.entries()]
      .map(([componentInstanceId, beforeSnapshot]) => {
        if (componentInstanceId === primaryComponentInstanceId) {
          return null;
        }

        const afterSnapshot = this.mateSolveLatestSnapshots.get(componentInstanceId);
        if (!afterSnapshot) {
          return null;
        }

        return createAssemblyTransformHistoryRecord(operation, beforeSnapshot, afterSnapshot);
      })
      .filter((record): record is AssemblyTransformHistoryRecord => Boolean(record));
  }

  /**
   * 回滚 Mate 实时求解移动过的组件。
   */
  private rollbackMateSolvedPreviewObjects(): void {
    this.mateSolveInitialSnapshots.forEach((snapshot) => {
      const object = this.viewer.assemblyMateController?.resolveSceneObjectForComponentSnapshot?.(snapshot) ?? null;
      if (!object) {
        return;
      }
      applyComponentInstanceSnapshotToObject(object, snapshot);
    });
  }

  /**
   * 清理 Mate 实时求解快照。
   */
  private clearMateSolveSnapshots(): void {
    this.mateSolveInitialSnapshots.clear();
    this.mateSolveLatestSnapshots.clear();
  }

  //------ 装配体基准引用移动后重算 开始------
  /**
   * 重算依赖当前装配体位姿的基准引用。
   *
   * 设计说明：
   * 1. 移动零部件的预览阶段已经改变目标对象的 `matrixWorld`，关联基准引用必须同步复算；
   * 2. 这里由移动服务统一触发，确保 Delta、装配体坐标、绝对坐标和取消回滚走同一条链路；
   * 3. 真正的引用解析和求解仍由各基准控制器负责，移动服务只声明“位姿已变化”。
   */
  private recomputeDependentAssemblyDatumReferences(): void {
    recomputeAssemblyDatumReferences(this.viewer);
  }

  /** 直接更新移动预览实际影响的实例矩阵槽，再请求普通场景渲染。 */
  private requestRenderAfterTransform(): void {
    this.updateChangedInstanceMatrices();
    // 矩阵是即时业务结果；只合并画面请求，不能把位姿写入延迟到下一帧。
    this.viewer.requestRender();
  }

  /**
   * 收集主移动目标与 Mate 联动目标，并直接同步对应实例矩阵。
   *
   * 取消预览时 `mateSolveInitialSnapshots` 表示刚恢复的对象；正常预览时 latest 表示求解后的
   * 对象。两组都只用于解析对象引用，Set 会消除同一组件的重复条目。
   */
  private updateChangedInstanceMatrices(): void {
    const changedObjects = new Set<THREE.Object3D>();
    if (this.previewSnapshot?.target) {
      changedObjects.add(this.previewSnapshot.target);
    }
    const mateSnapshots = [
      ...this.mateSolveInitialSnapshots.values(),
      ...this.mateSolveLatestSnapshots.values(),
    ];
    mateSnapshots.forEach((snapshot) => {
      const object = this.viewer.assemblyMateController
        ?.resolveSceneObjectForComponentSnapshot?.(snapshot) as THREE.Object3D | null | undefined;
      if (object) {
        changedObjects.add(object);
      }
    });
    this.viewer.updateImportedModelInstanceMatrices?.([...changedObjects]);
  }
  //------ 装配体基准引用移动后重算 结束------
}
